import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buildMenuTree, flattenMenuTree } from 'utils/menuTree';
import './TreeSelect.scss';

const getId = (item) => String(item.id ?? item.Id);

const defaultLabel = (item) =>
  item.title ||
  item.name ||
  item.label ||
  item.translations?.[0]?.title ||
  item.translations?.[0]?.name ||
  `Menu #${getId(item)}`;

const TreeSelect = ({
  label,
  name,
  value,
  onChange,
  items = [],
  placeholder = 'Chọn menu...',
  required = false,
  disabled = false,
  error = '',
  getLabel = defaultLabel,
  allowClear = true,
  className = '',
}) => {
  const rootRef = useRef(null);
  const tree = useMemo(() => buildMenuTree(items), [items]);
  const flatItems = useMemo(() => flattenMenuTree(tree), [tree]);
  const selectedItem = flatItems.find((item) => getId(item) === String(value));
  const [isOpen, setIsOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!value) return;
    const parentByChild = new Map();
    flatItems.forEach((item) =>
      (item.children || []).forEach((child) =>
        parentByChild.set(getId(child), getId(item)),
      ),
    );
    const ancestors = [];
    let parentId = parentByChild.get(String(value));
    while (parentId) {
      ancestors.push(parentId);
      parentId = parentByChild.get(parentId);
    }
    if (ancestors.length) {
      setExpanded((current) => new Set([...current, ...ancestors]));
    }
  }, [flatItems, value]);

  const emitChange = (nextValue) => {
    onChange?.({ target: { name, value: nextValue } });
    setIsOpen(false);
  };

  const toggleNode = (event, id) => {
    event.stopPropagation();
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderNodes = (nodes, depth = 0) =>
    nodes.map((item) => {
      const id = getId(item);
      const children = item.children || [];
      const hasChildren = children.length > 0;
      const isExpanded = expanded.has(id);
      const isSelected = id === String(value);

      return (
        <React.Fragment key={id}>
          <div
            className={`tree-select__item ${isSelected ? 'is-selected' : ''}`}
            role="treeitem"
            aria-selected={isSelected}
            aria-expanded={hasChildren ? isExpanded : undefined}
            style={{ '--tree-depth': depth }}
          >
            {hasChildren ? (
              <button
                type="button"
                className="tree-select__toggle"
                onClick={(event) => toggleNode(event, id)}
                aria-label={isExpanded ? 'Thu gọn menu con' : 'Mở menu con'}
              >
                <span className={isExpanded ? 'is-expanded' : ''}>›</span>
              </button>
            ) : (
              <span className="tree-select__leaf" />
            )}
            <button
              type="button"
              className="tree-select__option"
              onClick={() => emitChange(id)}
            >
              <span className="tree-select__node-label">{getLabel(item)}</span>
              {hasChildren && (
                <span className="tree-select__count">{children.length}</span>
              )}
              {isSelected && <span className="tree-select__check">✓</span>}
            </button>
          </div>
          {hasChildren && isExpanded && renderNodes(children, depth + 1)}
        </React.Fragment>
      );
    });

  return (
    <div
      ref={rootRef}
      className={`tree-select ${error ? 'has-error' : ''} ${className}`}
    >
      {label && (
        <label>
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}
      <button
        type="button"
        className={`tree-select__trigger ${isOpen ? 'is-open' : ''}`}
        onClick={() => !disabled && setIsOpen((current) => !current)}
        disabled={disabled}
        aria-haspopup="tree"
        aria-expanded={isOpen}
      >
        <span className={selectedItem ? '' : 'is-placeholder'}>
          {selectedItem ? getLabel(selectedItem) : placeholder}
        </span>
        <span className="tree-select__chevron">⌄</span>
      </button>
      {isOpen && (
        <div className="tree-select__panel">
          <div className="tree-select__panel-head">
            <span>Chọn theo cây menu</span>
            <div>
              <button
                type="button"
                onClick={() =>
                  setExpanded(
                    new Set(
                      flatItems
                        .filter((item) => item.children?.length)
                        .map(getId),
                    ),
                  )
                }
              >
                Mở tất cả
              </button>
              <button type="button" onClick={() => setExpanded(new Set())}>
                Thu gọn
              </button>
            </div>
          </div>
          <div className="tree-select__tree" role="tree">
            {allowClear && (
              <button
                type="button"
                className="tree-select__clear"
                onClick={() => emitChange('')}
              >
                — Không chọn —
              </button>
            )}
            {tree.length ? (
              renderNodes(tree)
            ) : (
              <p className="tree-select__empty">Chưa có dữ liệu menu</p>
            )}
          </div>
        </div>
      )}
      {error && <span className="error-message">{error}</span>}
    </div>
  );
};

export default TreeSelect;

